import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { filter, fromEvent, map, switchMap, takeUntil, tap } from 'rxjs';

import { useSubscriptions } from '@/hook';

import { VFlex } from '../VFlex';
import { useVTheme } from '../VThemeProvider';
import {
  VTree,
  type VTreeNode,
  type VTreeRef,
  makeVTreeNodeList,
} from '../VTree';
import { Folder } from '../asset';
import { joinCn } from '../util';
import { useFileBrowserContext, useFileBrowserScrollContext } from './context';
import style from './style';

const VFileBrowserTree = () => {
  const { themeName } = useVTheme();
  const {
    currentDirUuid,
    emptyRenderer,
    eventEmitter,
    itemByUuid,
    itemList,
    selectedItemNodeList,
    vTreeRef,
    // getParentListByItem,
    selectItemList,
    setCurrentDirUuid,
  } = useFileBrowserContext();
  const { $scrollable } = useFileBrowserScrollContext();
  const $resizer = useRef<HTMLDivElement>(null);
  const nodeList = useMemo(
    () =>
      makeVTreeNodeList(
        itemList,
        {
          children: 'children',
          id: 'uuid',
          label: 'name',
          disabled: 'disabled',
        },
        ({ isDir }) => !!isDir,
      ),
    [itemList],
  );
  const isEmpty = useMemo(() => !nodeList.length, [nodeList.length]);

  useEffect(() => {
    const currentNode = currentDirUuid
      ? vTreeRef.current?.nodeById[currentDirUuid]
      : null;

    if (!currentNode || !vTreeRef.current) return;

    vTreeRef.current.selectNode([currentNode]);
  }, [currentDirUuid, nodeList, vTreeRef]);

  useEffect(() => {
    const currentNode = currentDirUuid
      ? vTreeRef.current?.nodeById[currentDirUuid]
      : null;

    if (!currentNode || !vTreeRef.current) return;

    vTreeRef.current.openNode(currentNode ? [currentNode] : []);
  }, [currentDirUuid, nodeList, vTreeRef]);

  useEffect(() => {
    // TODO: .filter(v => v?.isDir) 임시 처방
    const selectedDirList = selectedItemNodeList.orgList.filter(v => v?.isDir);
    const selectedNodeList = selectedDirList
      .map(v => vTreeRef.current?.nodeById[v.uuid])
      ?.filter(v => v) as VTreeNode[];

    if (
      !selectedDirList.length ||
      !vTreeRef.current ||
      !selectedNodeList?.length
    )
      return;

    // TODO: setTimeout 임시 처방
    setTimeout(() => {
      vTreeRef.current!.selectNode(selectedNodeList);
    }, 0);
  }, [nodeList, selectedItemNodeList, vTreeRef]);

  useSubscriptions(
    () => [
      fromEvent<Omit<MouseEvent, 'target'> & { target: HTMLElement }>(
        window,
        'mousedown',
      )
        .pipe(
          filter(e => !!$resizer.current && e.target === $resizer.current),
          tap(e => e.preventDefault()),
          map(e => e.clientX),
          switchMap(sx => {
            const $tree = $resizer.current!.parentElement!;
            let width = $tree.style.width
              ? +$tree.style.width.replace('px', '')
              : $tree.offsetWidth;

            return fromEvent<MouseEvent>(window, 'mousemove').pipe(
              map(e => e.clientX),
              tap(ex => {
                const vW = ex - sx;

                width = width + vW;

                $tree.style.width = `${width}px`;

                sx = ex;
              }),
              takeUntil(fromEvent(window, 'mouseup')),
              takeUntil(fromEvent(window, 'blur')),
            );
          }),
        )
        .subscribe(),
    ],
    [],
  );

  useLayoutEffect(() => {
    const $tree = $resizer.current?.parentElement;

    if (!$tree) return;

    $tree.style.width = `${16 * 21}px`;

    if (vTreeRef.current && vTreeRef.current.el) {
      $scrollable.current = vTreeRef.current.el;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={style.vFileBrowserTree}
      data-testid="v-file-browser-tree-container"
    >
      <div className="h-full w-full" data-testid="v-file-browser-tree-wrapper">
        {!isEmpty ? (
          <VTree
            ref={vTreeRef as React.RefObject<VTreeRef>}
            autoOpen={false}
            className={joinCn(
              'h-full min-w-[324px] overflow-auto rounded-md border',
              themeName === 'VueFlow'
                ? 'border-[#00E88E]/10'
                : 'border-tertiary-200',
            )}
            nodeList={nodeList}
            renderNode={node => {
              const token = itemByUuid[node.id]?.name
                ?.replace(/\/$/, '')
                ?.split('/');

              return (
                <VFlex items="center" gap={0.25}>
                  <Folder className="h-5 w-5 text-fileType-folder" />
                  <div>{token?.[token.length - 1]}</div>
                </VFlex>
              );
            }}
            onSelect={e => {
              if (e.trigger !== 'click') return;

              // TODO: .filter(v => v) 임시 처방
              const selectedNodeList = e.target.result.filter(v => v);

              if (!selectedNodeList.length) {
                selectItemList([]);
                return;
              }

              const idList = selectedNodeList.map(n => n.id);
              const uuid = idList[0];

              eventEmitter.emit('itemClick', {
                src: 'tree',
                item: itemByUuid[idList[0]],
              });

              selectItemList(idList);
              setCurrentDirUuid(uuid);
            }}
            onClose={e => {
              // TODO: .filter(v => v) 임시 처방
              const closedNodeList = e.target.result.filter(v => v);

              if (!closedNodeList.length) return;

              const uuid = closedNodeList[0].id;

              eventEmitter.emit('closeDirectory', { item: itemByUuid[uuid] });
            }}
            onOpen={e => {
              // TODO: .filter(v => v) 임시 처방
              const opendNodeList = e.target.result.filter(v => v);

              if (!opendNodeList.length) return;

              const uuid = opendNodeList[0].id;

              eventEmitter.emit('openDirectory', { item: itemByUuid[uuid] });
            }}
          />
        ) : (
          <div
            className={joinCn(
              'h-full min-w-[324px] overflow-auto rounded-md border',
              themeName === 'VueFlow'
                ? 'border-[#00E88E]/10'
                : 'border-tertiary-200',
            )}
          >
            {emptyRenderer.tree()}
          </div>
        )}
      </div>

      <div ref={$resizer} className={style.vFileBrowserTreeResizer}></div>
    </div>
  );
};

export default VFileBrowserTree;
